import { useEffect, useState } from "react";
import { Settings2, Play, Pause, CalendarRange, Save, Music2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";

const KIND_LABEL = { saying: "Saying cards", flyer: "Flyers", video: "Stock/motion videos", clip: "AI cinematic clips ($)", game: "Audience games" };
const inputCls = "h-9 w-full rounded-md bg-[#0A0A0A] border border-[#27272A] px-2 text-sm text-white font-mono focus:outline-none focus:border-[#FF3B30]";

/** Volume dial for the Official Stage autopilot. */
export function AutopilotSettings({ onChanged }) {
  const [s, setS] = useState(null);
  const [perDay, setPerDay] = useState({});
  const [langs, setLangs] = useState([]);
  const [mode, setMode] = useState("review");
  const [social, setSocial] = useState(12);
  const [pexels, setPexels] = useState("");
  const [pixabay, setPixabay] = useState("");
  const [busy, setBusy] = useState(false);
  const [days, setDays] = useState(14);

  const load = async () => {
    try {
      const { data } = await api.get("/official/settings");
      setS(data); setPerDay(data.per_day); setLangs(data.languages); setMode(data.mode); setSocial(data.social_per_day);
    } catch { /* admin only */ }
  };
  const planningNow = s?.planning && !s.planning.finished;
  useEffect(() => { load(); }, []);
  useEffect(() => { const t = setInterval(load, planningNow ? 4000 : 30000); return () => clearInterval(t); }, [planningNow]);

  const save = async (only = null) => {
    setBusy(true);
    try {
      const body = only || { mode, per_day: Object.fromEntries(Object.entries(perDay).map(([k, v]) => [k, Number(v) || 0])), languages: langs, social_per_day: Number(social) || 0 };
      if (!only && pexels.trim()) body.pexels_key = pexels.trim();
      if (!only && pixabay.trim()) body.pixabay_key = pixabay.trim();
      const { data } = await api.put("/official/settings", body);
      setS(data); if (!only) { setPexels(""); setPixabay(""); }
      toast.success(only ? (only.paused ? "Autopilot paused" : "Autopilot resumed") : "Autopilot settings saved");
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const backfill = async () => {
    setBusy(true);
    try { await api.post("/official/backfill", { days: Number(days) }); toast.success(`Planning ${days} days of content in the background`); load(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  if (!s) return null;
  const total = Object.values(perDay).reduce((a, b) => a + Number(b || 0), 0);
  const st = s.stats || {};
  const planning = s.planning && !s.planning.finished;

  return (
    <div data-testid="autopilot-settings" className="mt-5 rounded-xl border border-white/10 bg-black/30 p-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-neutral-400"><Settings2 className="h-4 w-4" /> Volume engine</div>
        <span data-testid="autopilot-total" className="text-xs font-mono text-white">{total} pieces / day · {langs.length} language{langs.length === 1 ? "" : "s"}</span>
        <span className="text-[11px] text-neutral-500" data-testid="autopilot-stats">
          today: {st.scheduled_today ?? 0} scheduled · {st.published_today ?? 0} published · queue: {st.render?.queued ?? 0} to render{st.render?.rendering ? " (rendering…)" : ""}{st.render?.failed ? ` · ${st.render.failed} failed` : ""} · {st.planned_days ?? 0} day{st.planned_days === 1 ? "" : "s"} planned
        </span>
        <div className="ml-auto flex items-center gap-2">
          <Button size="sm" variant="outline" data-testid="autopilot-pause-btn" disabled={busy} onClick={() => save({ paused: !s.paused })} className={`rounded-full border-white/20 ${s.paused ? "text-[#34C759]" : "text-[#FF9F0A]"}`}>
            {s.paused ? <><Play className="h-4 w-4 mr-1" /> Resume</> : <><Pause className="h-4 w-4 mr-1" /> Pause</>}
          </Button>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-3">
        {Object.keys(KIND_LABEL).map((k) => (
          <label key={k} className="text-[11px] text-neutral-400">
            {KIND_LABEL[k]}
            <input type="number" min={0} max={500} value={perDay[k] ?? 0} onChange={(e) => setPerDay({ ...perDay, [k]: e.target.value })} data-testid={`autopilot-per-day-${k}`} className={`${inputCls} mt-1`} />
          </label>
        ))}
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <span className="text-[11px] text-neutral-400 mr-1">Languages:</span>
        {Object.entries(s.all_languages).map(([code, name]) => (
          <button key={code} data-testid={`autopilot-lang-${code}`} onClick={() => setLangs(langs.includes(code) ? langs.filter((l) => l !== code) : [...langs, code])}
            className={`px-2.5 py-1 rounded-full text-[11px] border ${langs.includes(code) ? "border-white bg-white text-black" : "border-white/15 text-neutral-300"}`}>{name}</button>
        ))}
      </div>

      <div className="mt-3 grid md:grid-cols-5 gap-3 items-end">
        <label className="text-[11px] text-neutral-400">Publishing mode
          <select value={mode} onChange={(e) => setMode(e.target.value)} data-testid="autopilot-mode" className={`${inputCls} mt-1`}>
            <option value="review">Review first — auto-publish after 24h</option>
            <option value="auto">Fully automatic — publish on schedule</option>
          </select>
        </label>
        <label className="text-[11px] text-neutral-400">Social posts / day (Telegram · X · Meta)
          <input type="number" min={0} max={200} value={social} onChange={(e) => setSocial(e.target.value)} data-testid="autopilot-social" className={`${inputCls} mt-1`} />
        </label>
        <label className="text-[11px] text-neutral-400">Pixabay API key (free stock footage)
          <input type="password" value={pixabay} onChange={(e) => setPixabay(e.target.value)} data-testid="autopilot-pixabay" placeholder={s.pixabay_key_set ? "Saved ✓ — paste to replace" : "pixabay.com/api/docs → your key"} className={`${inputCls} mt-1`} />
        </label>
        <label className="text-[11px] text-neutral-400">Pexels API key (optional, if you have one)
          <input type="password" value={pexels} onChange={(e) => setPexels(e.target.value)} data-testid="autopilot-pexels" placeholder={s.pexels_key_set ? "Saved ✓ — paste to replace" : "Pexels paused new keys — skip"} className={`${inputCls} mt-1`} />
        </label>
        <Button data-testid="autopilot-save-btn" onClick={() => save()} disabled={busy} className="h-9 rounded-full bg-white text-black hover:bg-neutral-200 font-bold"><Save className="h-4 w-4 mr-1" /> Save settings</Button>
      </div>

      {s.stats?.memory?.low_memory && (
        <div className="mt-3 rounded-lg border border-[#FF9F0A]/40 bg-[#FF9F0A]/10 p-3 text-xs text-[#ffd27a]" data-testid="autopilot-lowmem-banner">
          <span className="font-bold">Images only right now.</span> This server has {s.stats.memory.limit_mb} MB of memory; video and music rendering needs about {s.stats.memory.needed_mb} MB. Sayings, flyers and games keep publishing. Raise the tier in Deployment Panel → Resources and redeploy to switch videos back on — the queued videos will render automatically.
        </div>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-3 text-[11px] text-neutral-400" data-testid="autopilot-music">
        <Music2 className="h-4 w-4 text-[#34C759]" />
        <span>Music beds: <span className="text-white font-mono">{s.music?.ready ?? 0}/{s.music?.target ?? 24}</span> ready — licence-free, generated once and mixed under every video (ducked beneath the voice).{(s.music?.ready ?? 0) < (s.music?.target ?? 24) ? " The worker is composing the rest in the background." : ""}</span>
        {s.music?.sample_id && (
          <audio data-testid="autopilot-music-sample" controls preload="none" className="h-7 max-w-[220px]" src={`${process.env.REACT_APP_BACKEND_URL}/api/official/music/${s.music.sample_id}`} title={s.music.sample_mood} />
        )}
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-white/10 pt-3">
        <CalendarRange className="h-4 w-4 text-[#FF9F0A]" />
        <span className="text-xs text-neutral-300">Fill the Stage ahead of time:</span>
        <select value={days} onChange={(e) => setDays(e.target.value)} data-testid="autopilot-backfill-days" className="h-9 rounded-md bg-[#0A0A0A] border border-[#27272A] px-2 text-sm text-white">
          {Array.from({ length: 30 }, (_, i) => i + 1).map((d) => <option key={d} value={d}>{d} day{d === 1 ? "" : "s"}</option>)}
        </select>
        <Button size="sm" data-testid="autopilot-backfill-btn" onClick={backfill} disabled={busy || planning} className="rounded-full bg-[#FF9F0A] hover:bg-[#e68f09] text-black font-bold">
          {planning ? `Planning… ${s.planning.done}/${s.planning.days} days` : `Plan ${days} days now`}
        </Button>
        <span className="text-[11px] text-neutral-500">Text + cards are prepared for every day; videos and AI clips render automatically within 36h of their slot.</span>
      </div>
    </div>
  );
}
